import {Component, OnInit, Output, EventEmitter, Input} from '@angular/core';
import {AppOutput} from '../../users-api';
import {UntypedFormControl, Validators} from '@angular/forms';
import {StorageService} from '../../services/storage.service';
import {AppsUtilService} from '../../services/apps-util.service';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';

interface AppOption {
  appId: string;
  appLabel: string;
}

@Component({
  selector: 'app-app-selector',
  templateUrl: './app-selector.component.html',
  styleUrls: ['./app-selector.component.scss']
})
export class AppSelectorComponent implements OnInit {
  @Output() onAppSelected = new EventEmitter<string>();

  @Input()
  set currentAppId(appId: string) {
    this.currentAppIdParamUsed = true;
    this.appSelectControl.setValue(appId);
    if (appId) {
      this.setSelectedApp(appId);
    }
  }

  @Input() restrictToIds: string[] | undefined;
  @Input() disabled = false;
  @Input() NoSetLastSelected = false;
  appSelectControl = new UntypedFormControl(Validators.required);
  appsOptions: AppOption[];
  filteredAppsOptions: Observable<AppOption[]>;
  currentAppIdParamUsed: boolean;

  constructor(
    private storageService: StorageService,
    private appsUtilService: AppsUtilService,
  ) {
    this.currentAppIdParamUsed = false;
  }

  setSelectedApp(appId: string) {
    if (appId) {
      this.storageService.set('AppSelectorComponent:lastSelectedApp', String(appId));
    } else {
      this.storageService.delete('AppSelectorComponent:lastSelectedApp');
    }
    this.onAppSelected.emit(appId ? String(appId) : appId);
  }

  displayFn = (appId: any): string => {
    if (!appId || !this.appsOptions) {
      return appId ? String(appId) : '';
    }
    const strAppId = String(appId);
    const selectedApp = this.appsOptions.find(app => String(app.appId) === strAppId);
    return selectedApp ? selectedApp.appLabel : strAppId;
  };

  private _filter(value: any): AppOption[] {
    if (!this.appsOptions) return [];
    const filterValue = value ? String(value).toLowerCase() : '';
    return this.appsOptions.filter(option =>
      option.appLabel.toLowerCase().includes(filterValue) ||
      String(option.appId).toLowerCase().includes(filterValue)
    );
  }

  async loadAppsOptions() {
    try {
      const apps = await this.appsUtilService.getApps();
      this.appsOptions = apps.map((app: AppOutput) => ({
        appId: String(app.id),
        appLabel: `${app.name} (${app.id})`
      }));

      this.filteredAppsOptions = this.appSelectControl.valueChanges.pipe(
        startWith(this.appSelectControl.value || ''),
        map(value => this._filter(value))
      );

    } catch (error) {
      console.error('AppSelectorComponent: getting apps list', error);
    }
    if (this.NoSetLastSelected) return;
    const lastSelected = this.storageService.get('AppSelectorComponent:lastSelectedApp');
    if (lastSelected) {
      this.appSelectControl.setValue(lastSelected);
    }
  }

  ngOnInit() {
    if (!this.currentAppIdParamUsed) {
      this.appSelectControl.setValue('');
    }
    this.loadAppsOptions();
    this.appSelectControl.markAllAsTouched();
    this.appSelectControl.valueChanges.subscribe(
      (value: any) => {
        if (!value) {
          this.setSelectedApp('');
        } else {
          const strValue = String(value);
          const isValidApp = this.appsOptions?.some(a => String(a.appId) === strValue);
          if (isValidApp) {
            this.setSelectedApp(strValue);
          }
        }
      }
    );
  }

}
